import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  NOTIFICATION_CATALOG,
  notificationPreferenceGroupToTypes,
  userNotificationPreferencesQueryKey,
} from "@/lib/constants";
import { guacHttpClient } from "@/lib/http";
import {
  HttpResponse,
  NotificationType,
  UpdateUserNotificationPreferenceRequest,
  UserNotificationPreferenceResponse,
} from "@/types";

export const useUpdateSetUserNotificationPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...request
    }: { id: number } & UpdateUserNotificationPreferenceRequest) => {
      const path = `user-notification-preferences/${id.toString()}`;
      const res = await guacHttpClient.patch<
        UserNotificationPreferenceResponse,
        HttpResponse<UserNotificationPreferenceResponse>,
        UpdateUserNotificationPreferenceRequest
      >(path, request);
      return res.data;
    },
    onSuccess: (updatedPreference) => {
      // Guac writes every member of a preference group together, so mirror the
      // new channel state onto the siblings instead of only the updated row.
      const group =
        NOTIFICATION_CATALOG[updatedPreference.type]?.preferenceGroup;
      const siblingTypes = group
        ? new Set(notificationPreferenceGroupToTypes[group])
        : new Set<NotificationType>();

      queryClient.setQueryData<UserNotificationPreferenceResponse[]>(
        userNotificationPreferencesQueryKey,
        (cachedData) =>
          cachedData?.map((preference) => {
            if (preference.id === updatedPreference.id) {
              return updatedPreference;
            }
            if (!siblingTypes.has(preference.type)) {
              return preference;
            }
            return {
              ...preference,
              emailEnabled: updatedPreference.emailEnabled,
              inAppEnabled: updatedPreference.inAppEnabled,
              slackEnabled: updatedPreference.slackEnabled,
            };
          }),
      );
    },
  });
};
